import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, FileText } from 'lucide-react';
import Marquee from '../components/Marquee';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const packages = [
  {
    name: 'Wedding Day Director',
    price: 'Call for Pricing',
    badge: '',
    tagline: 'Day-Of Excellence',
    features: [
      '10 hours on-site coverage',
      '1 hour rehearsal coordination',
      'Timeline creation & management',
      'Vendor coordination & communication',
      'On-site producer & staff',
      'Day-of emergency kit',
    ],
    cta: 'Get Started',
    highlighted: false,
  },
  {
    name: 'Partial Planning',
    price: 'Call for Pricing',
    badge: '',
    tagline: 'For Couples Finishing The Details',
    features: [
      '90 days prior to the wedding date',
      'For couples that need help finishing up wedding plans',
      'Includes 1 hour rehearsal coordination',
      '10 hour on-site wedding management',
      'Vendor review & recommendations',
      'Budget guidance & support',
    ],
    cta: 'Get Started',
    highlighted: false,
  },
  {
    name: 'Full Service',
    price: 'Call for Pricing',
    badge: '',
    tagline: 'Complete White-Glove Experience',
    features: [
      'When you need someone to take the reins!',
      'Unlimited meetings and communication at signing',
      'Up to 14 hours on-site wedding management',
      'Full vendor sourcing & management',
      'Design direction & styling',
      'Budget creation & oversight',
      'Concierge guest experience',
    ],
    cta: 'Get Started',
    secondaryCta: 'Detailed Service Tasks',
    highlighted: true,
  },
  {
    name: 'Ala Carte Hourly Support',
    price: '$89\u2013$150',
    badge: 'Starting at',
    tagline: 'Minimum of 5 Hours Required \u00b7 Invoiced in Advance of Services',
    features: [
      'Starter Package \u2014 Where to begin with a great timeline and budget.',
      'Assist with wedding logistics as requested',
      'Offer expert guidance on any topic or issue resolution',
      'On-call with any topic or task',
      'You decide what to work on when you need the support \u2014 no pressure!',
    ],
    cta: 'Get Started',
    highlighted: false,
  },
];

const WeddingPlanning = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#0a0a0a] min-h-screen text-white overflow-x-hidden">
      <SEO
        title="Wedding Planning Packages — Day-Of, Partial & Full Service | Dion Magee Events"
        description="Tiered wedding planning packages — Wedding Day Director, Partial Planning, and Full Service. Bespoke coordination, design, and production by Sir Dion Magee Events & Weddings. Call for pricing."
        path="/wedding-planning"
        image="/og-image.jpg"
      />
      {/* Top header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0a]/85 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1 group">
            <img
              src="/dm-logo.png"
              alt="Dion Magee Events & Weddings"
              className="w-16 h-16 md:w-20 md:h-20 object-contain transition-transform duration-500 group-hover:scale-105"
            />
            <div className="hidden sm:block leading-none">
              <div className="text-white font-serif text-[22px] md:text-[26px] tracking-[0.18em] leading-none">
                DION MAGEE
              </div>
              <div className="text-[#c9a96e]/90 text-[10px] md:text-[11px] tracking-[0.32em] mt-1 leading-none">
                EVENTS &amp; WEDDINGS
              </div>
            </div>
          </Link>

          <div className="flex items-center gap-3 md:gap-6">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-2 text-white/80 hover:text-[#c9a96e] text-[11px] tracking-[0.3em] uppercase transition-colors"
            >
              <ArrowLeft size={14} /> Back To Home
            </Link>
            <Link
              to="/#contact"
              className="text-[#c9a96e] hover:bg-[#c9a96e] hover:text-black px-5 md:px-7 py-3 border border-[#c9a96e]/60 text-[10px] md:text-[11px] tracking-[0.3em] uppercase transition-colors"
            >
              Enquire Now
            </Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-[88vh] w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1523438885200-e635ba2c371e?w=2200&q=85"
            alt="Wedding ceremony"
            className="w-full h-full object-cover scale-105 animate-[wpZoom_14s_ease-out_forwards]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/65 via-transparent to-black/40" />
          {/* Ambient gold glow */}
          <div
            className="absolute -top-32 -left-32 w-[55vw] h-[55vw] rounded-full opacity-25 blur-3xl pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(232,213,168,0.6) 0%, transparent 70%)',
              animation: 'wpOrbA 24s ease-in-out infinite',
              mixBlendMode: 'screen',
            }}
          />
          <div
            className="absolute -bottom-32 -right-20 w-[45vw] h-[45vw] rounded-full opacity-20 blur-3xl pointer-events-none"
            style={{
              background:
                'radial-gradient(circle, rgba(255,210,150,0.55) 0%, transparent 70%)',
              animation: 'wpOrbB 30s ease-in-out infinite',
              mixBlendMode: 'screen',
            }}
          />
        </div>

        <div className="relative z-10 h-full min-h-[88vh] flex items-end pb-24 px-6 lg:px-16">
          <div className="max-w-[1500px] mx-auto w-full">
            <p className="text-[#c9a96e] text-[10px] md:text-[11px] tracking-[0.5em] uppercase mb-8 anim-up break-words">
              Services &nbsp;—&nbsp; Wedding Planning
            </p>
            <h1
              className="font-serif text-white text-[42px] sm:text-[68px] md:text-[88px] lg:text-[110px] leading-[0.95] tracking-tight anim-up break-words"
              style={{ animationDelay: '0.15s' }}
            >
              Wedding
            </h1>
            <h1
              className="font-serif text-white text-[42px] sm:text-[68px] md:text-[88px] lg:text-[110px] leading-[0.95] tracking-tight anim-up break-words"
              style={{ animationDelay: '0.3s' }}
            >
              Planning &amp;
            </h1>
            <h1
              className="font-serif italic text-[#e8c98a] text-[42px] sm:text-[68px] md:text-[88px] lg:text-[110px] leading-[0.95] tracking-tight -mt-2 anim-up break-words"
              style={{
                animationDelay: '0.45s',
                fontFamily: 'Cormorant Garamond, serif',
              }}
            >
              Coordination
            </h1>

            <p
              className="mt-12 max-w-xl text-white/85 text-base md:text-lg font-light leading-relaxed anim-up"
              style={{ animationDelay: '0.6s' }}
            >
              Expert logistics. Seamless execution. Memories that last a lifetime.
            </p>

            <div
              className="mt-12 flex flex-col sm:flex-row gap-4 anim-up"
              style={{ animationDelay: '0.75s' }}
            >
              <a
                href="#packages"
                className="group bg-[#e8d5a8] hover:bg-[#c9a96e] text-black px-9 py-4 text-[11px] tracking-[0.3em] uppercase inline-flex items-center justify-center gap-3 transition-colors"
              >
                Begin Your Story
                <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#packages"
                className="border border-white/40 hover:border-[#c9a96e] hover:text-[#c9a96e] text-white px-9 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors"
              >
                View Packages
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee strip */}
      <Marquee />

      {/* OUR APPROACH */}
      <section className="bg-[#0a0a0a] py-28 lg:py-40 px-6 lg:px-12">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <div className="relative">
            {/* Floating diamond accent — favicon-style sparkling jewel */}
            <div className="absolute -top-6 -left-2 md:-top-10 md:-left-6 w-16 h-16 md:w-24 md:h-24 pointer-events-none z-0">
              <div
                className="absolute inset-0 rounded-full blur-2xl opacity-70"
                style={{
                  background:
                    'radial-gradient(circle, rgba(232,213,168,0.7) 0%, transparent 70%)',
                  animation: 'wpDiamondPulse 4s ease-in-out infinite',
                }}
              />
              <img
                src="/diamond-accent.png"
                alt=""
                aria-hidden="true"
                className="relative w-full h-full object-contain"
                style={{
                  filter:
                    'drop-shadow(0 0 14px rgba(255,255,255,0.55)) drop-shadow(0 0 32px rgba(232,213,168,0.5))',
                  animation: 'wpDiamondFloat 5s ease-in-out infinite',
                }}
              />
              <span
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[180%] h-px bg-gradient-to-r from-transparent via-white/65 to-transparent"
                style={{ animation: 'wpSparkleFade 3.5s ease-in-out infinite' }}
              />
              <span
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[180%] w-px bg-gradient-to-b from-transparent via-white/65 to-transparent"
                style={{ animation: 'wpSparkleFade 3.5s ease-in-out 0.6s infinite' }}
              />
            </div>

            <p className="relative text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-8">
              Our Approach
            </p>
            <h2 className="relative font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
              Your Vision,
            </h2>
            <h2
              className="relative font-serif italic text-[#c9a96e] text-5xl md:text-6xl lg:text-7xl leading-[1.05] -mt-1"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Our Expertise
            </h2>

            <div className="mt-10 space-y-6 text-white/75 text-[15px] leading-[1.95] font-light max-w-xl">
              <p>
                As award-winning event &amp; wedding producers, we excel in wedding planning by delivering personalized, seamless experiences. Our team's expertise, creativity, and attention to detail ensure every event is memorable and stress-free.
              </p>
              <p>
                With our one-stop service, we design incredible wedding experiences to match your style and taste. Trust us to turn your vision into reality with unmatched professionalism and passion.
              </p>
            </div>

            <Link
              to="/#contact"
              className="mt-12 inline-flex items-center gap-3 text-[#c9a96e] text-[11px] tracking-[0.35em] uppercase border-b border-[#c9a96e]/40 hover:border-[#c9a96e] pb-2 transition-colors"
            >
              Schedule A Consultation
              <ArrowRight size={14} />
            </Link>
          </div>

          <div className="relative">
            <div className="relative aspect-[4/5] w-full max-w-[560px] mx-auto group">
              {/* Sparkling diamond accent on top-right corner */}
              <div className="absolute -top-6 -right-3 md:-top-10 md:-right-8 w-16 h-16 md:w-24 md:h-24 z-30 pointer-events-none">
                <div
                  className="absolute inset-0 rounded-full blur-2xl opacity-80"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(232,213,168,0.8) 0%, transparent 70%)',
                    animation: 'wpDiamondPulse 5s ease-in-out 1s infinite',
                  }}
                />
                <img
                  src="/diamond-accent.png"
                  alt=""
                  aria-hidden="true"
                  className="relative w-full h-full object-contain"
                  style={{
                    filter:
                      'drop-shadow(0 0 16px rgba(255,255,255,0.7)) drop-shadow(0 0 32px rgba(232,213,168,0.55))',
                    animation: 'wpDiamondFloat 6s ease-in-out 0.8s infinite',
                  }}
                />
                <span
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[170%] h-px bg-gradient-to-r from-transparent via-white/70 to-transparent"
                  style={{ animation: 'wpSparkleFade 3.5s ease-in-out 1.2s infinite' }}
                />
                <span
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[170%] w-px bg-gradient-to-b from-transparent via-white/70 to-transparent"
                  style={{ animation: 'wpSparkleFade 3.5s ease-in-out 1.8s infinite' }}
                />
              </div>

              {/* Glow */}
              <div
                className="absolute -inset-8 rounded-[30%] opacity-60 blur-3xl pointer-events-none"
                style={{
                  background:
                    'radial-gradient(ellipse at center, rgba(232,213,168,0.4) 0%, transparent 70%)',
                }}
              />
              <div
                className="relative w-full h-full overflow-hidden z-10"
                style={{
                  boxShadow:
                    '0 0 60px rgba(232,213,168,0.2), 0 20px 60px rgba(0,0,0,0.6)',
                }}
              >
                <img
                  src="/vision-expertise.jpg"
                  alt="Bride and wedding guests toasting with champagne"
                  className="w-full h-full object-cover transition-transform duration-[2000ms] group-hover:scale-[1.04]"
                />
                <div
                  className="absolute inset-0 mix-blend-soft-light pointer-events-none"
                  style={{
                    background:
                      'linear-gradient(135deg, rgba(232,213,168,0.3) 0%, transparent 40%, transparent 60%, rgba(201,169,110,0.25) 100%)',
                  }}
                />
              </div>
              {/* 25+ Years badge */}
              <div className="absolute -bottom-8 left-2 md:-bottom-12 md:-left-12 bg-[#e8d5a8] text-black px-7 md:px-10 py-6 md:py-8 z-20 shadow-2xl">
                <div className="font-serif text-3xl md:text-5xl leading-none">25+</div>
                <div className="text-[9px] md:text-[10px] tracking-[0.35em] uppercase mt-3 whitespace-nowrap">
                  Years of Excellence
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING & PACKAGES */}
      <section id="packages" className="bg-[#0a0a0a] py-28 lg:py-40 px-6 lg:px-12">
        <div className="max-w-[1300px] mx-auto">
          <div className="text-center mb-20">
            <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">
              Investment
            </p>
            <h2 className="font-serif text-white text-5xl md:text-6xl lg:text-7xl leading-[1.05]">
              Pricing &amp;{' '}
              <span
                className="italic text-[#c9a96e]"
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                Packages
              </span>
            </h2>
            <p className="text-white/65 mt-8 leading-[1.9] font-light max-w-xl mx-auto text-[15px]">
              Three carefully curated packages designed to match your needs and give you the support you deserve.
            </p>
          </div>

          <div className="space-y-px border border-white/10">
            {packages.map((pkg) => (
              <div
                key={pkg.name}
                className={`relative grid grid-cols-1 md:grid-cols-12 ${
                  pkg.highlighted
                    ? 'bg-gradient-to-r from-[#1a1408] to-[#0f0a04] border-l-2 border-[#c9a96e]'
                    : 'bg-[#0a0a0a]'
                }`}
              >
                {pkg.highlighted && (
                  <div className="absolute -top-px right-4 md:right-12 bg-[#e8d5a8] text-black text-[9px] md:text-[10px] tracking-[0.3em] md:tracking-[0.35em] uppercase px-4 md:px-6 py-2 z-10">
                    Most Popular
                  </div>
                )}

                {/* Price column */}
                <div className="md:col-span-3 p-10 md:p-12 lg:p-14 border-b md:border-b-0 md:border-r border-white/10 flex flex-col justify-center">
                  {pkg.badge && (
                    <p className="text-white/40 text-[10px] tracking-[0.4em] uppercase mb-4">
                      {pkg.badge}
                    </p>
                  )}
                  <div
                    className={`font-serif leading-[1.05] ${
                      pkg.price.startsWith('$')
                        ? 'text-5xl md:text-6xl'
                        : 'text-3xl md:text-4xl'
                    } ${pkg.highlighted ? 'text-[#c9a96e]' : 'text-white'}`}
                    style={{ fontFamily: 'Cormorant Garamond, serif' }}
                  >
                    {pkg.price}
                  </div>
                  <p className="text-white/45 text-[10px] tracking-[0.3em] uppercase mt-6 leading-[1.8]">
                    {pkg.tagline}
                  </p>
                </div>

                {/* Features column */}
                <div className="md:col-span-6 p-10 md:p-12 lg:p-14">
                  <h3
                    className={`text-xl md:text-2xl tracking-[0.18em] uppercase mb-7 font-medium ${
                      pkg.highlighted ? 'text-[#c9a96e]' : 'text-white'
                    }`}
                  >
                    {pkg.name}
                  </h3>
                  <ul className="space-y-4">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-3 text-white/75 text-[14px] font-light">
                        <Check
                          size={14}
                          className={`mt-1.5 flex-shrink-0 ${
                            pkg.highlighted ? 'text-[#c9a96e]' : 'text-white/40'
                          }`}
                          strokeWidth={2}
                        />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA column */}
                <div className="md:col-span-3 p-10 md:p-12 lg:p-14 flex flex-col justify-center gap-4 border-t md:border-t-0 md:border-l border-white/10">
                  <Link
                    to="/#contact"
                    className={`group inline-flex items-center justify-center gap-2 px-6 py-4 text-[11px] tracking-[0.3em] uppercase transition-colors ${
                      pkg.highlighted
                        ? 'bg-[#e8d5a8] hover:bg-[#c9a96e] text-black'
                        : 'border border-white/25 hover:border-[#c9a96e] hover:text-[#c9a96e] text-white'
                    }`}
                  >
                    {pkg.cta}
                  </Link>
                  {pkg.secondaryCta && (
                    <Link
                      to="/service-tasks"
                      className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-white/25 hover:border-[#c9a96e] hover:text-[#c9a96e] text-white text-[10px] tracking-[0.3em] uppercase transition-colors"
                    >
                      <FileText size={12} />
                      {pkg.secondaryCta}
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-white/40 text-[11px] tracking-[0.2em] uppercase mt-12 max-w-2xl mx-auto leading-[1.9]">
            All prices are starting rates. Final pricing based on event scope, guest count, and location. Contact us for a custom quote.
          </p>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="relative overflow-hidden bg-[#0a0a0a] py-28 lg:py-40 px-6 lg:px-12">
        <div
          className="absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1519741497674-611481863552?w=2400&q=85')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Softer overlays so the wedding photo shows through */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/85 via-[#0a0a0a]/55 to-[#0a0a0a]/90" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/70 via-transparent to-[#0a0a0a]/70" />
        {/* Warm gold ambient orb to enhance romance */}
        <div
          className="absolute -top-32 left-1/4 w-[50vw] h-[50vw] rounded-full blur-3xl opacity-25 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle, rgba(232,213,168,0.55) 0%, transparent 70%)',
            mixBlendMode: 'screen',
          }}
        />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <p className="text-[#c9a96e] text-[11px] tracking-[0.5em] uppercase mb-6">
            Begin Your Story
          </p>
          <h2 className="font-serif text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1]">
            Let's design the most
          </h2>
          <h2
            className="font-serif italic text-[#c9a96e] text-4xl md:text-5xl lg:text-6xl leading-[1.1] mt-1"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            beautiful day of your life
          </h2>
          <Link
            to="/#contact"
            className="mt-12 inline-flex items-center gap-3 bg-[#e8d5a8] hover:bg-[#c9a96e] text-black px-10 py-4 text-[11px] tracking-[0.35em] uppercase transition-colors"
          >
            Start The Conversation
            <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <style>{`
        @keyframes wpZoom {
          from { transform: scale(1.12); }
          to { transform: scale(1.02); }
        }
        @keyframes wpOrbA {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(15%, 12%); }
        }
        @keyframes wpOrbB {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(-12%, -10%); }
        }
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .anim-up {
          opacity: 0;
          transform: translateY(28px);
          animation: wpFadeUp 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        @keyframes wpFadeUp {
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes wpDiamondPulse {
          0%, 100% { opacity: 0.55; transform: scale(1); }
          50%      { opacity: 0.95; transform: scale(1.12); }
        }
        @keyframes wpDiamondFloat {
          0%, 100% { transform: translateY(0) rotate(-2deg); }
          50%      { transform: translateY(-6px) rotate(2deg); }
        }
        @keyframes wpSparkleFade {
          0%, 100% { opacity: 0; transform: translate(-50%, -50%) scaleX(0.5); }
          50%      { opacity: 0.9; transform: translate(-50%, -50%) scaleX(1); }
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default WeddingPlanning;
